import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { z } from 'zod';
import { useRegister } from '../../hooks/useAuth';
import { AuthShell } from '../components/auth/AuthShell';
import { SocialAuthButtons } from '../components/auth/SocialAuthButtons';
import { EmailField, FormAlert, NameField, PasswordField, PhoneField, SubmitButton } from '../components/auth/fields';
import { authErrorKind, authErrorText } from '../components/auth/authErrors';
import { isValidKgPhone, toApiPhone } from '../components/auth/phone';

type RegisterForm = { name: string; email: string; phone: string; password: string };

const MIN_PASSWORD = 6;

export function Register() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const prefillEmail = (location.state as { email?: string } | null)?.email ?? '';

  // Built per language so validation messages follow a language switch.
  const schema = useMemo(
    () =>
      z.object({
        name: z.string().trim().min(2, t('authFlow.nameRequired', 'Введите имя')),
        email: z
          .string()
          .trim()
          .min(1, t('authFlow.emailRequired', 'Введите email'))
          .pipe(z.email(t('profile.errors.invalidEmail', 'Некорректный email адрес'))),
        phone: z.string().refine(isValidKgPhone, t('authFlow.phoneInvalid', 'Введите 9 цифр номера, например 555 12 34 56')),
        password: z
          .string()
          .min(MIN_PASSWORD, t('authFlow.passwordMin', { count: MIN_PASSWORD, defaultValue: 'Минимум {{count}} символов' })),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language],
  );

  const {
    register,
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<RegisterForm>({
    resolver: zodResolver(schema),
    defaultValues: { email: prefillEmail, phone: '' },
  });

  const registration = useRegister();
  const kind = registration.isError ? authErrorKind(registration.error) : null;

  const onSubmit = (data: RegisterForm) => registration.mutate({ ...data, phone: toApiPhone(data.phone) });

  return (
    <AuthShell wide title={t('register.title')} subtitle={t('register.subtitle')}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate data-compact className="flex flex-col gap-3 auth-tiny:gap-2.5 auth-tall:gap-4">
        {/* Two columns from tablets up: Name | Phone, Email | Password. */}
        <div className="grid grid-cols-1 gap-3 auth-tiny:gap-2 auth-tall:gap-4 sm:grid-cols-2 sm:gap-x-4">
          <NameField
            label={t('authFlow.name', 'Имя')}
            placeholder={t('register.namePlaceholder')}
            enterKeyHint="next"
            error={errors.name?.message}
            {...register('name')}
          />

          <EmailField
            label={t('register.email')}
            placeholder="email@example.com"
            enterKeyHint="next"
            error={errors.email?.message}
            {...register('email')}
          />

          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <PhoneField
                label={t('register.phone')}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.phone?.message}
                hint={t('authFlow.phoneHint', 'Покупатели смогут позвонить вам по объявлению')}
                hintOnFocus
              />
            )}
          />

          <PasswordField
            label={t('register.password')}
            placeholder={t('register.passwordPlaceholder')}
            autoComplete="new-password"
            enterKeyHint="done"
            error={errors.password?.message}
            hint={t('authFlow.passwordMin', { count: MIN_PASSWORD, defaultValue: 'Минимум {{count}} символов' })}
            {...register('password')}
          />
        </div>

        {kind && (
          <FormAlert>
            {authErrorText(t, kind === 'notFound' || kind === 'wrongPassword' ? 'unknown' : kind, t('register.registrationFailed'))}
            {kind === 'emailTaken' && (
              <>
                {' '}
                <Link
                  to="/login"
                  state={{ email: getValues('email'), from: (location.state as { from?: unknown } | null)?.from }}
                  className="font-semibold underline"
                >
                  {t('register.signIn')}
                </Link>
              </>
            )}
          </FormAlert>
        )}

        <SubmitButton busy={registration.isPending}>{t('register.createAccount')}</SubmitButton>
      </form>

      <SocialAuthButtons />

      <p className="mt-3 auth-short:mt-2 text-center text-xs auth-tiny:text-[11px] leading-relaxed auth-tiny:leading-snug text-gray-500 text-balance auth-tall:mt-4 auth-tall:text-[13px]">
        {t('authFlow.termsImplicit', 'Регистрируясь, вы принимаете')}{' '}
        <Link to="/terms" className="font-semibold text-blue-600 hover:text-blue-700">
          {t('authFlow.termsLink1', 'Условия использования')}
        </Link>{' '}
        {t('register.termsAnd')}{' '}
        <Link to="/privacy" className="font-semibold text-blue-600 hover:text-blue-700">
          {t('authFlow.termsLink2', 'Политику конфиденциальности')}
        </Link>
      </p>

      <p className="mt-3 auth-short:mt-1.5 text-center text-sm text-gray-500 auth-tall:mt-5 auth-tall:text-[15px]">
        {t('register.already')}{' '}
        <Link to="/login" state={location.state} className="font-bold text-blue-600 hover:text-blue-700">
          {t('register.signIn')}
        </Link>
      </p>
    </AuthShell>
  );
}
